import { IMembershipUpdateProps } from "@/api_framework/api_modals/Membership";
import Input from "@/app/components/ui/Inputs/Input";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { useMembershipDetailsAPI } from "@/app/hooks/api_hooks/Membership/useMembershipDetailsAPI";
import { useMembershipDisplayAPI } from "@/app/hooks/api_hooks/Membership/useMembershipDisplayAPI";
import { useUpdateMembershipAPI } from "@/app/hooks/api_hooks/Membership/useUpdateMembershipAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { Switch } from "@headlessui/react";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { BiSolidOffer } from "react-icons/bi";
import { MdOutlineDone } from "react-icons/md";
import { PiSparkleFill } from "react-icons/pi";
import { useNavigate, useSearchParams } from "react-router-dom";

const tabs = [
  { name: "VMS PREVIEW", href: "#", current: true },
  { name: "APP PREVIEW", href: "#", current: false },
];

export interface IMembershipCmsCouponListFromFieldgeneral_details {
  membership_title?: string;
  price?: number;
}

export interface IMembershipCmsCouponListFromFielder_benefits {
  display_heading?: string;
  coupon_name?: string;
  coupon_id?: string;
  display?: boolean;
}

export interface IMembershipCmsCouponListFromFielder_yearly_saving {
  saving_copy?: string;
  saving_price?: string;
}

export interface IMembershipCmsCouponListFromFieldbenefit_details {
  coupon_name: string;
  coupon_id: string;
  feature_name: string;
  member_price: number | null;
  member_total_price: number | null;
  usage_limit: string | number | null;
  non_member_price: number | null;
  non_member_total_price: number | null;
  display_service_price: string;
}

export interface IMembershipCmsCouponFromField {
  general_details?: IMembershipCmsCouponListFromFieldgeneral_details;
  benefit_details?: IMembershipCmsCouponListFromFieldbenefit_details[];
  other_benefit?: IMembershipCmsCouponListFromFielder_benefits[];
  yearly_saving?: IMembershipCmsCouponListFromFielder_yearly_saving;
}

const MembershipCMSComponent = () => {
  const navigate = useNavigate();
  const { forAlphaNumeric, forNumericWithDecimalAndNotStartFromZero } = useFormValidations();
  const [params, _setParams] = useSearchParams();
  const membership_id = params.get("membership_id");
  const [isInitial, setIsInitial] = useState<boolean>(false);
  const { execute: fetchMembershipDetails, membershipDetails } = useMembershipDetailsAPI();
  const { execute: fetchmembershipDisplay, membershipDisplay } = useMembershipDisplayAPI();
  const { execute: updateMembership } = useUpdateMembershipAPI();
  const membershipDisplayItem = useSelectMenuReducer(membershipDisplay, "name", "id");

  const formHook = useForm<IMembershipCmsCouponFromField>({
    mode: "onChange",
    defaultValues: {
      benefit_details: [],
      other_benefit: [],
      yearly_saving: {
        saving_price: "",
        saving_copy: "Total Yearly Savings",
      },
    },
  });

  const couponFormArray = useFieldArray<IMembershipCmsCouponFromField>({
    name: "benefit_details",
    control: formHook.control,
  });

  const othercouponFormArray = useFieldArray<IMembershipCmsCouponFromField>({
    name: "other_benefit",
    control: formHook.control,
  });

  const onSubmit = (data: IMembershipCmsCouponFromField) => {
    const constructedBody: IMembershipUpdateProps = {
      cms_preview_details: data,
    };
    updateMembership(constructedBody, membership_id).then(({ status }) => {
      if (status) {
        navigate(`/app/membership-setup/membership-list`);
      }
    });
  };

  useEffect(() => {
    fetchmembershipDisplay();
  }, []);

  useEffect(() => {
    if (membershipDetails?.cms_preview_details && !isInitial) {
      formHook.setValue("general_details.price", membershipDetails?.general_details?.amount);
      formHook.setValue(
        "general_details.membership_title",
        membershipDetails?.cms_preview_details?.general_details?.membership_title,
      );
      formHook.setValue(
        "yearly_saving.saving_copy",
        membershipDetails?.cms_preview_details?.yearly_saving?.saving_copy
          ? membershipDetails?.cms_preview_details?.yearly_saving?.saving_copy
          : "Total Yearly Savings",
      );
      formHook.setValue(
        "yearly_saving.saving_price",
        membershipDetails?.cms_preview_details?.yearly_saving?.saving_price?.toString(),
      );
      const benefit_details = membershipDetails?.cms_preview_details?.benefit_details;
      const other_benefit = membershipDetails?.cms_preview_details?.other_benefit;
      couponFormArray.append(benefit_details);
      othercouponFormArray.append(other_benefit);
      setIsInitial(true);
    }
  }, [membershipDetails, isInitial]);

  useEffect(() => {
    if (membership_id) {
      fetchMembershipDetails(membership_id);
    }
  }, [membership_id]);

  const handelBackStepper = () => {
    if (membership_id) {
      navigate(
        `/app/membership-setup/membership-creation?page_step=add_coupon&&membership_id=${membership_id}&&membership_applicable=${params.get("membership_applicable")}`,
      );
    } else {
      navigate(`/app/membership-setup/membership-creation?page_step=add_coupon&&membership_id=${membership_id}`);
    }
  };

  return (
    <form className="p-6 bg-white w-full mt-4" onSubmit={formHook.handleSubmit(onSubmit, (e) => console.log(e))}>
      <div className="grid grid-cols-12">
        <div className="col-span-8 overflow-scroll h-[80vh]">
          <div>
            <h2 className="text-sm font-semibold pl-1">Details</h2>
            <div className="mt-2 flex flex-col gap-4 w-full">
              <div className="flex items-start">
                <div className="w-2/12">
                  <p>
                    Membership Title <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-3/6">
                  <Input
                    className="text-xs"
                    placeholder="Add Title"
                    register={formHook.register("general_details.membership_title", {
                      required: true,
                      validate: (value) => value.trim() !== "" || "Name cannot be empty or only spaces",
                    })}
                    fieldError={formHook.formState.errors.general_details?.membership_title}
                    errorMessages={[
                      { message: "Membership Title is required", type: "required" },
                      { message: "Name cannot be empty or only spaces", type: "validate" },
                    ]}
                  />
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-2/12">
                  <p>Membership Price</p>
                </div>
                <div className="w-3/12">
                  <p className="text-xs">{formHook.getValues("general_details.price")}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="py-5">
            <hr />
          </div>
          {couponFormArray?.fields?.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold pl-1">Key benefits</h2>
              {couponFormArray?.fields?.map((filed, index) => {
                return (
                  <div key={filed.id} className="w-full h-auto p-3 rounded-lg border border-gray-200 my-3">
                    <p className="text-xs mb-3 font-medium">
                      {formHook.getValues(`benefit_details.${index}.coupon_name` as const)}
                    </p>
                    <div className="grid grid-cols-4 gap-5 pt-2">
                      <div className="col-span-2">
                        <div className="grid grid-cols-2 items-center">
                          <p>
                            Feature Name <span className="text-red-400">*</span>
                          </p>
                          <p>
                            {" "}
                            <Input
                              className="text-xs"
                              placeholder="Add Feature Name"
                              register={formHook.register(`benefit_details.${index}.feature_name` as const, {
                                required: true,
                              })}
                              fieldError={
                                formHook?.formState?.errors?.benefit_details
                                  ? formHook?.formState?.errors?.benefit_details[index]
                                    ? formHook?.formState?.errors?.benefit_details[index]?.feature_name
                                    : null
                                  : null
                              }
                              errorMessages={[
                                { message: "Feature Name is required", type: "required" },
                                { message: "Name cannot be empty or only spaces", type: "validate" },
                              ]}
                            />
                          </p>
                        </div>
                        <div className="grid grid-cols-2 mt-5 items-center">
                          <p className="text-xs">
                            Member price/service <span className="text-red-400">*</span>
                          </p>
                          <p>
                            {" "}
                            <Input
                              className="text-xs"
                              placeholder="Add Price"
                              register={formHook.register(`benefit_details.${index}.member_price` as const, {
                                required: true,
                                ...forNumericWithDecimalAndNotStartFromZero.validations,
                              })}
                              fieldError={
                                formHook?.formState?.errors?.benefit_details
                                  ? formHook?.formState?.errors?.benefit_details[index]
                                    ? formHook?.formState?.errors?.benefit_details[index]?.member_price
                                    : null
                                  : null
                              }
                              errorMessages={[
                                { message: "", type: "required" },
                                forNumericWithDecimalAndNotStartFromZero.errors,
                              ]}
                              onChange={(e) => {
                                const target = e.target as HTMLInputElement;
                                const memberPrice = Number(target.value) || 0;
                                const quantity =
                                  Number(formHook.getValues(`benefit_details.${index}.usage_limit` as const)) || 0;
                                const memberTotalPrice = memberPrice * quantity;
                                formHook.setValue(`benefit_details.${index}.member_total_price`, memberTotalPrice);
                                formHook.clearErrors(`benefit_details.${index}.member_price`);
                              }}
                            />
                          </p>
                        </div>
                        <div className="grid grid-cols-2 mt-3 items-center">
                          <p>
                            Member total price <span className="text-red-400">*</span>
                          </p>
                          <p>
                            {" "}
                            <Input
                              className="text-xs"
                              placeholder="Add Price"
                              register={formHook.register(`benefit_details.${index}.member_total_price` as const, {
                                required: true,
                                ...forNumericWithDecimalAndNotStartFromZero.validations,
                              })}
                              readOnly
                              fieldError={
                                formHook?.formState?.errors?.benefit_details
                                  ? formHook?.formState?.errors?.benefit_details[index]
                                    ? formHook?.formState?.errors?.benefit_details[index]?.member_total_price
                                    : null
                                  : null
                              }
                              errorMessages={[
                                { message: "", type: "required" },
                                forNumericWithDecimalAndNotStartFromZero.errors,
                              ]}
                              onChange={() => {
                                formHook.clearErrors(`benefit_details.${index}.member_total_price`);
                              }}
                            />
                          </p>
                        </div>
                        <div className="grid grid-cols-2 mt-3 items-center">
                          <p>
                            Display <span className="text-red-400">*</span>
                          </p>
                          <p>
                            {" "}
                            <NonSearchableSelectMenu
                              errorMessages={[{ message: "", type: "required" }]}
                              onSelectItem={(item) => {
                                if (item) {
                                  formHook.setValue(`benefit_details.${index}.display_service_price`, item.title);
                                }
                              }}
                              fieldError={
                                formHook?.formState?.errors?.benefit_details
                                  ? formHook?.formState?.errors?.benefit_details[index]
                                    ? formHook?.formState?.errors?.benefit_details[index]?.display_service_price
                                    : null
                                  : null
                              }
                              register={formHook.register(`benefit_details.${index}.display_service_price`, {
                                required: true,
                              })}
                              selectItems={membershipDisplayItem}
                              placeholder="Display"
                              showTooltips={false}
                              showTypedErrors={false}
                              showDropdownIcon={true}
                              defaultSelected={
                                membershipDisplayItem?.filter(
                                  (oc) => oc.title === formHook.watch(`benefit_details.${index}.display_service_price`),
                                )[0]
                              }
                              listBoxClassName="w-full"
                              className="text-gray-800"
                              containerClassName="w-full"
                            />{" "}
                          </p>
                        </div>
                      </div>
                      <div className="col-span-2 items-center">
                        <div className="grid grid-cols-2">
                          <p>Max usage per user</p>
                          <p>{formHook.getValues(`benefit_details.${index}.usage_limit` as const)} </p>
                        </div>
                        <div className="grid grid-cols-2 mt-6 items-center">
                          <p>
                            Non-Member price/service <span className="text-red-400">*</span>
                          </p>
                          <p>
                            {" "}
                            <Input
                              className="text-xs"
                              placeholder="Add Price"
                              register={formHook.register(`benefit_details.${index}.non_member_price` as const, {
                                required: true,
                                ...forNumericWithDecimalAndNotStartFromZero.validations,
                              })}
                              fieldError={
                                formHook?.formState?.errors?.benefit_details
                                  ? formHook?.formState?.errors?.benefit_details[index]
                                    ? formHook?.formState?.errors?.benefit_details[index]?.non_member_price
                                    : null
                                  : null
                              }
                              errorMessages={[
                                { message: "", type: "required" },
                                forNumericWithDecimalAndNotStartFromZero.errors,
                              ]}
                              onChange={(e) => {
                                const target = e.target as HTMLInputElement;
                                const nonMemberPrice = Number(target.value) || 0;
                                const quantity =
                                  Number(formHook.getValues(`benefit_details.${index}.usage_limit` as const)) || 0;
                                const nonMemberTotalPrice = nonMemberPrice * quantity;
                                formHook.setValue(
                                  `benefit_details.${index}.non_member_total_price`,
                                  nonMemberTotalPrice,
                                );
                                formHook.clearErrors(`benefit_details.${index}.non_member_price`);
                              }}
                            />
                          </p>
                        </div>
                        <div className="grid grid-cols-2 mt-3 items-center">
                          <p>
                            Non-Member total price <span className="text-red-400">*</span>
                          </p>
                          <p>
                            {" "}
                            <Input
                              className="text-xs"
                              placeholder="Add Price"
                              register={formHook.register(`benefit_details.${index}.non_member_total_price` as const, {
                                required: true,
                                ...forNumericWithDecimalAndNotStartFromZero.validations,
                              })}
                              fieldError={
                                formHook?.formState?.errors?.benefit_details
                                  ? formHook?.formState?.errors?.benefit_details[index]
                                    ? formHook?.formState?.errors?.benefit_details[index]?.non_member_total_price
                                    : null
                                  : null
                              }
                              errorMessages={[
                                { message: "", type: "required" },
                                forNumericWithDecimalAndNotStartFromZero.errors,
                              ]}
                              value={formHook.getValues(`benefit_details.${index}.non_member_total_price` as const)}
                            />
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div>
                <h2 className="text-sm font-semibold py-3">Savings due to key benefits</h2>
                <div className="mt-2 flex flex-col gap-4 w-full pb-3">
                  <div className="flex items-start">
                    <div className="w-2/6">
                      <p>Format as per Data</p>
                    </div>
                    <div className="w-full">
                      <p>[Feature Name 1, Feature Name 2] @ [NM Total Price M Total Price]. [Savings] Saved !</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="py-6">
                <hr />
              </div>
            </div>
          )}
          <div>
            <h2 className="text-sm font-semibold pl-1">Other Benefits</h2>
            {othercouponFormArray?.fields?.length > 0 && (
              <div className="w-4/5">
                {" "}
                <div className="grid grid-cols-3 gap-8 mt-4">
                  <p className="w-1/4">Coupon</p>
                  <p>
                    Display Heading <span className="text-red-400">*</span>
                  </p>
                  <p>Display</p>
                </div>
                {othercouponFormArray?.fields?.map((feild, index) => {
                  return (
                    <div className="grid grid-cols-3 mt-4 gap-8" key={feild?.id}>
                      <p>{formHook.getValues(`other_benefit.${index}.coupon_name`)}</p>
                      <Input
                        className="text-xs"
                        placeholder="Add Display Heading"
                        defaultValue={formHook.getValues(`other_benefit.${index}.display_heading`)}
                        register={formHook.register(`other_benefit.${index}.display_heading`, {
                          required: true,
                          validate: (value) => value.trim() !== "" || "Name cannot be empty or only spaces",
                        })}
                        fieldError={
                          formHook?.formState?.errors?.other_benefit
                            ? formHook?.formState?.errors?.other_benefit[index]
                              ? formHook?.formState?.errors?.other_benefit[index]?.display_heading
                              : null
                            : null
                        }
                        errorMessages={[
                          { message: "Display Heading is required", type: "required" },
                          { message: "Name cannot be empty or only spaces", type: "validate" },
                        ]}
                      />
                      <Switch
                        checked={formHook.watch(`other_benefit.${index}.display`)}
                        onChange={() => {
                          if (formHook.watch(`other_benefit.${index}.display`)) {
                            formHook.setValue(`other_benefit.${index}.display`, false);
                          } else {
                            formHook.setValue(`other_benefit.${index}.display`, true);
                          }
                        }}
                        className={`${formHook.watch(`other_benefit.${index}.display`) ? "bg-[#4D9FFF]" : "bg-[#CCCCCC]"}
        relative inline-flex h-6 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75`}
                      >
                        <span
                          aria-hidden="true"
                          className={`${formHook.watch(`other_benefit.${index}.display`) ? "translate-x-8" : "translate-x-0"}
          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                        />
                      </Switch>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="mt-6">
            <hr />
          </div>
          <div className="mt-4">
            <h2 className="text-sm font-semibold py-3">Yearly Savings</h2>
            <div className="mt-2 flex flex-col gap-4 w-full">
              <div className="flex items-start">
                <div className="w-2/12">
                  <p>Savings Copy</p>
                </div>
                <div className="w-3/12">
                  <Input
                    className="text-xs"
                    placeholder="Add Savings Copy"
                    register={formHook.register("yearly_saving.saving_copy", {
                      required: true,
                      ...forAlphaNumeric.validations,
                    })}
                    fieldError={formHook.formState.errors.yearly_saving?.saving_copy}
                    errorMessages={[{ message: "", type: "required" }, forAlphaNumeric.errors]}
                  />
                </div>
              </div>
            </div>
            <div className="mt-3 flex flex-col gap-4 w-full">
              <div className="flex items-start">
                <div className="w-2/12">
                  <p>
                    Yearly Savings <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-3/12">
                  <Input
                    className="text-xs"
                    placeholder="Add Amount"
                    register={formHook.register("yearly_saving.saving_price", {
                      required: true,
                      ...forNumericWithDecimalAndNotStartFromZero.validations,
                    })}
                    fieldError={formHook.formState.errors.yearly_saving?.saving_price}
                    errorMessages={[{ message: "", type: "required" }, forNumericWithDecimalAndNotStartFromZero.errors]}
                    onChange={(event) => {
                      const inputValue = (event.target as HTMLInputElement).value;
                      formHook.setValue("yearly_saving.saving_price", inputValue);
                      formHook.clearErrors(`yearly_saving.saving_price`);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-4">
          <div className="mx-8">
            <div className="hidden sm:block">
              <nav aria-label="Tabs" className="isolate flex">
                {tabs?.map((tab, tabIdx) => (
                  <a
                    key={tab.name}
                    href={tab.href}
                    aria-current={tab.current ? "page" : undefined}
                    className={classNames(
                      tab.current ? "text-gray-900" : "text-gray-500 hover:text-gray-700",
                      tabIdx === 0 ? "rounded-l-lg" : "",
                      tabIdx === tabs.length - 1 ? "rounded-r-lg" : "",
                      "group relative min-w-0 flex-1 overflow-hidden bg-[#F8F8F8] px-4 py-4 text-center text-sm font-medium hover:bg-gray-50 focus:z-10",
                    )}
                  >
                    <span>{tab.name}</span>
                    <span
                      aria-hidden="true"
                      className={classNames(
                        tab.current ? "bg-indigo-500" : "bg-transparent",
                        "absolute inset-x-0 bottom-0 h-0.5",
                      )}
                    />
                  </a>
                ))}
              </nav>
            </div>
            <div className="bg-[#fafaff] border border-[#9b9de5] my-4 rounded-3xl py-4 px-3">
              <div className="w-full">
                <p className="text-base text-[#565ADD] font-medium break-words">
                  {formHook.watch("general_details.membership_title") || "Add Title"}
                </p>
              </div>

              <p className="text-xl py-1 text-[#565ADD] font-semibold">
                @{formHook.getValues("general_details.price")}
              </p>
              <div className="py-2">
                <p className="text-base text-[#1C242F] font-medium flex items-center gap-2">
                  <PiSparkleFill className="w-4 h-4 fill-[#565ADD]" /> Key Benefits
                </p>
              </div>
              <div>
                <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
                  <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                    <div className="overflow-hidden ring-1 ring-black ring-opacity-5 sm:rounded-md">
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-[#dadaef]">
                          <tr>
                            <th
                              scope="col"
                              className="py-3.5 whitespace-break-spaces pl-4 pr-3 text-left text-xs font-medium text-black"
                            >
                              Features
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium text-black">
                              Members
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-xs font-medium text-black">
                              Non Members
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {couponFormArray?.fields?.map((filed, index) => {
                            return (
                              <tr key={filed.id}>
                                <td className="whitespace-break-spaces px-3 py-3.5 text-xs text-black sm:pl-4">
                                  {formHook.watch(`benefit_details.${index}.feature_name`) || "Add Feature"}
                                </td>
                                <td className="whitespace-nowrap px-3 py-3.5 text-xs text-black">
                                  {formHook.watch(`benefit_details.${index}.display_service_price`) ===
                                  "Price per Service"
                                    ? `${formHook.watch(`benefit_details.${index}.member_price`)} /Service`
                                    : formHook.watch(`benefit_details.${index}.member_total_price`)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-3.5 text-xs text-black">
                                  {formHook.watch(`benefit_details.${index}.display_service_price`) ===
                                  "Price per Service"
                                    ? formHook.watch(`benefit_details.${index}.non_member_price`)
                                    : formHook.watch(`benefit_details.${index}.non_member_total_price`)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-[#565ADD] font-medium p-2 text-sm flex items-center gap-2 border my-3 rounded-md border-[#9fa2f4]">
                <BiSolidOffer className="w-4 h-4" />
                {couponFormArray?.fields?.length > 0 ? (
                  <p className="text-xs flex items-center">
                    {couponFormArray?.fields
                      ?.map((field, index) => {
                        const featureName = formHook.watch(`benefit_details.${index}.feature_name`);
                        const memberPrice = formHook.watch(`benefit_details.${index}.member_total_price`);
                        const nonMemberPrice = formHook.watch(`benefit_details.${index}.non_member_total_price`);
                        const savings = nonMemberPrice - memberPrice;
                        return `${featureName}`;
                      })
                      ?.join(", ")}
                    @
                    <div
                      className="flex items-center"
                      dangerouslySetInnerHTML={{
                        __html:
                          `<span class="line-through">${couponFormArray?.fields
                            ?.reduce((totalNonMember, field, index) => {
                              const nonMemberPrice: any = formHook.watch(
                                `benefit_details.${index}.non_member_total_price`,
                              );
                              return totalNonMember + (isNaN(nonMemberPrice) ? 0 : parseFloat(nonMemberPrice));
                            }, 0)
                            .toString()}</span>&nbsp;` +
                          +`${couponFormArray?.fields
                            ?.reduce((totalMember, field, index) => {
                              const memberPrice: any = formHook.watch(`benefit_details.${index}.member_total_price`);
                              return totalMember + (isNaN(memberPrice) ? 0 : parseFloat(memberPrice));
                            }, 0)
                            .toString()}`,
                      }}
                    />
                    .
                    {` ${couponFormArray?.fields?.reduce((totalSavings, field, index) => {
                      const memberPrice = formHook.watch(`benefit_details.${index}.member_total_price`);
                      const nonMemberPrice = formHook.watch(`benefit_details.${index}.non_member_total_price`);
                      const savings = nonMemberPrice - memberPrice;
                      return totalSavings + (isNaN(savings) ? 0 : savings);
                    }, 0)} Saved!`}
                  </p>
                ) : (
                  "Add member & non-member total prices"
                )}
              </p>
              <div>
                <p className="text-[#565ADD] font-semibold pt-2 text-base">OTHER BENEFITS</p>
                {othercouponFormArray?.fields?.map((filed, index) => {
                  return (
                    formHook.watch(`other_benefit.${index}.display`) && (
                      <p className="text-xs text-black py-1 flex items-center gap-2" key={filed.id}>
                        <MdOutlineDone className="fill-[#565ADD] w-4 h-4" />{" "}
                        {formHook.watch(`other_benefit.${index}.display_heading`) || "Add"}
                      </p>
                    )
                  );
                })}
              </div>
              <div className="pb-2 flex items-center justify-center mt-3">
                <p className="text-[#565ADD] font-semibold text-sm p-3 px-5 rounded-full text-center bg-[#cccdec]">
                  {formHook.watch("yearly_saving.saving_copy")} :{" "}
                  {formHook.watch("yearly_saving.saving_price") || "Add Value"}
                </p>
              </div>
            </div>
          </div>
          <div className="right-5 bottom-0 fixed flex justify-end items-center gap-4 border-t-2 w-full p-3 bg-white">
            <button
              type="button"
              onClick={() => handelBackStepper()}
              className="text-xs mt-3 inline-flex w-full justify-center rounded-md bg-[#b1b5bb] px-4 py-3 font-semibold text-white ring-inset ring-gray-300 hover-bg-gray-50 sm:mt-0 sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="text-xs mt-3 inline-flex w-full justify-center rounded-md bg-[#193A6A] px-4 py-3 font-semibold text-white ring-inset ring-gray-300 hover-bg-gray-50 sm:mt-0 sm:w-auto"
            >
              Save & Next
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default MembershipCMSComponent;
